import { Download } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, Grid, Paper, Typography } from "@mui/material";
import ExcelJS from "exceljs";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo } from "react";
import { InHouseLineChart } from "../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import type { InHouseLineDataGroup } from "../../components/UIToolkit/Graphs/LineChart/types";
import type { PbjCalculatorResponse } from "../../utils/models";

// Functional / Categorical: chart series colors. Must stay stable across themes.
// These feed `stroke` on an SVG <Line>, so every value has to be a real CSS color — an MUI theme
// path like "primary.dark" resolves in `sx` but not here, and silently renders no line at all.
const PBJ_CHART_SERIES = {
	HPPD: "var(--inhouse-primary-dark)",
	PPD: "var(--inhouse-primary-dark)",
	CENSUS: "#2196F3",
	AGENCY_PPD: "#E71C00",
} as const;

type PbjCalculatorDataProps = { data?: PbjCalculatorResponse; error: any; startMonth: DateTime; endMonth: DateTime };

export const PbjCalculatorData: FC<PbjCalculatorDataProps> = ({ data, error, startMonth, endMonth }) => {
	const handleDownloadExcel = useCallback(async (fileName: string, data: PbjCalculatorResponse) => {
		const workbook = new ExcelJS.Workbook();

		// ────────────────────────────────────────────────
		// WEEKLY DATA SHEET
		// ────────────────────────────────────────────────
		const weeklySheet = workbook.addWorksheet("Weekly Data");
		weeklySheet.columns = [
			{ header: "Week Start", key: "week_start", width: 15 },
			{ header: "Week End", key: "week_end", width: 15 },
			{ header: "Average HPPD", key: "avg_hppd", width: 15 },
			{ header: "Average PPD ($)", key: "avg_ppd", width: 15 },
			{ header: "Average Census", key: "avg_census", width: 15 },
			{ header: "Average Agency Cost ($)", key: "avg_agency_cost", width: 20 },
		];

		data.weekly_data.forEach((metric) => {
			weeklySheet.addRow({
				week_start: metric.week_start,
				week_end: metric.week_end,
				avg_hppd: metric.avg_hppd,
				avg_ppd: Math.round(metric.avg_ppd), // matches chart rounding
				avg_census: Math.round(metric.avg_census), // matches chart
				avg_agency_cost: Math.round(metric.avg_agency_cost / metric.avg_census || 0), // approximate PPD
			});
		});

		// Header styling
		const headerRow = weeklySheet.getRow(1);
		headerRow.font = { bold: true, color: { argb: "FFFFFF" } };
		headerRow.fill = {
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: "4F517A" },
		};

		// ────────────────────────────────────────────────
		// STATISTICS SHEET
		// ────────────────────────────────────────────────
		const statsSheet = workbook.addWorksheet("Statistics");
		statsSheet.columns = [
			{ header: "Statistic", key: "name", width: 40 },
			{ header: "Value", key: "value", width: 22 },
		];

		// Helper to round large money values to nearest $100
		const roundTo100 = (val: number) => Math.round(val / 100) * 100;

		// Facility Information
		statsSheet.addRow({ name: "Facility Information", value: "" });
		statsSheet.addRow({ name: "Facility Name", value: data.facility_info.provname });
		statsSheet.addRow({ name: "City, State", value: `${data.facility_info.city}, ${data.facility_info.state}` });
		statsSheet.addRow({ name: "CCN", value: data.facility_info.provnum });
		statsSheet.addRow({ name: "", value: "" });

		// General Statistics
		statsSheet.addRow({ name: "General Statistics", value: "" });
		statsSheet.addRow({ name: "Estimated $PPD", value: `$${data.estimated_ppd.toFixed(2)}` });
		statsSheet.addRow({ name: "Average Census", value: data.average_census.toFixed(0) });
		statsSheet.addRow({ name: "", value: "" });

		// Overtime Statistics
		statsSheet.addRow({ name: "Overtime Statistics", value: "" });
		statsSheet.addRow({ name: "Estimated $PPD from OT", value: `$${data.estimated_ppd_from_ot.toFixed(2)}` });
		statsSheet.addRow({
			name: "Total OT Cost (Annual)",
			value: `$${roundTo100(data.total_ot_cost).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
		});
		statsSheet.addRow({ name: "", value: "" });

		// Agency Statistics
		statsSheet.addRow({ name: "Agency Statistics", value: "" });
		statsSheet.addRow({ name: "Estimated $PPD from Agency", value: `$${data.estimated_ppd_from_agency.toFixed(2)}` });
		statsSheet.addRow({
			name: "Total Cost of Agency (Annual)",
			value: `$${roundTo100(data.total_cost_of_agency).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
		});
		statsSheet.addRow({ name: "", value: "" });

		// Overstaffing Statistics
		statsSheet.addRow({ name: "Overstaffing Statistics", value: "" });
		statsSheet.addRow({
			name: "Estimated HPPD from Overstaffing",
			value: data.estimated_hppd_from_overstaffing.toFixed(2),
		});
		statsSheet.addRow({
			name: "Total Overstaffed Hours (Annual)",
			value: data.total_overstaffed_hours.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
		});
		statsSheet.addRow({
			name: "Average Hourly Wage",
			value: `$${data.average_hourly_wage.toFixed(0)}`,
		});
		statsSheet.addRow({
			name: "Total Overstaffing Cost (Annual)",
			value: `$${roundTo100(data.total_overstaffing_cost).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
		});
		statsSheet.addRow({ name: "", value: "" });

		// Estimated Savings (rounded to nearest $100 to match cost consistency)
		statsSheet.addRow({ name: "Estimated Savings (Annual)", value: "" });
		statsSheet.addRow({
			name: "Reduction in OT",
			value: `$${roundTo100(data.reduction_in_ot).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
		});
		statsSheet.addRow({
			name: "Reduction in Agency",
			value: `$${roundTo100(data.reduction_in_agency).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
		});
		statsSheet.addRow({
			name: "Reduction in Overstaffing",
			value: `$${roundTo100(data.reduction_in_overstaffing).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
		});
		statsSheet.addRow({
			name: "Total Estimated Savings",
			value: `$${roundTo100(data.total_estimated_savings).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
		});

		// ── Section header styling ──
		const sectionHeaderRows = [1, 6, 10, 14, 20, 27];
		sectionHeaderRows.forEach((rowNum) => {
			const row = statsSheet.getRow(rowNum);
			row.font = { bold: true, size: 12 };
			row.fill = {
				type: "pattern",
				pattern: "solid",
				fgColor: { argb: "E0E0E0" },
			};
		});

		// Top header
		statsSheet.getRow(1).font = { bold: true, color: { argb: "FFFFFF" } };
		statsSheet.getRow(1).fill = {
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: "4F517A" },
		};

		// Optional: right-align numeric-looking values
		statsSheet.getColumn(2).alignment = { horizontal: "right" };

		// ────────────────────────────────────────────────
		// GENERATE & DOWNLOAD
		// ────────────────────────────────────────────────
		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${fileName}.xlsx`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	}, []);
	const hppdDataGroup = useMemo<InHouseLineDataGroup | undefined>(() => {
		if (!data?.weekly_data) return undefined;
		return {
			name: "HPPD",
			data: data.weekly_data.map((d) => ({ date: DateTime.fromISO(d.week_start), value: d.avg_hppd })),
			color: PBJ_CHART_SERIES.HPPD,
			graphCurve: "linear",
		};
	}, [data?.weekly_data]);

	const ppdDataGroup = useMemo<InHouseLineDataGroup | undefined>(() => {
		if (!data?.weekly_data) return;
		return {
			name: "PPD ($)",
			data: data.weekly_data.map((d) => ({ date: DateTime.fromISO(d.week_start), value: Math.round(d.avg_ppd) })),
			color: PBJ_CHART_SERIES.PPD,
			graphCurve: "linear",
		};
	}, [data?.weekly_data]);

	const agencyPpdDataGroup = useMemo<InHouseLineDataGroup | undefined>(() => {
		if (!data?.weekly_data) return;
		return {
			name: "Agency PPD ($)",
			data: data.weekly_data.map((d) => ({ date: DateTime.fromISO(d.week_start), value: Math.round(d.avg_agency_cost / d.avg_census) })),
			color: PBJ_CHART_SERIES.AGENCY_PPD,
			graphCurve: "linear",
		};
	}, [data?.weekly_data]);

	const censusDataGroup = useMemo<InHouseLineDataGroup | undefined>(() => {
		if (!data?.weekly_data) return;
		return {
			name: "Census",
			data: data.weekly_data.map((d) => ({ date: DateTime.fromISO(d.week_start), value: Math.round(d.avg_census) })),
			color: PBJ_CHART_SERIES.CENSUS,
			graphCurve: "linear",
		};
	}, [data?.weekly_data]);

	return (
		<>
			{error && (
				<Alert severity="error" sx={{ mb: 3 }}>
					{error?.response?.data?.detail?.message || error?.response?.data?.message || error?.message || "An error occurred"}
				</Alert>
			)}
			{data && (
				<Box>
					<Grid container spacing={3} sx={{ mb: 3 }}>
						<Grid size={{ xs: 12 }}>
							<Card>
								<CardContent>
									<Typography color="primary" variant="h6">
										{data.facility_info.provname}
									</Typography>
									<Typography
										variant="body2"
										sx={{
											color: "text.secondary",
										}}
									>
										{data.facility_info.city}, {data.facility_info.state}
									</Typography>
									<Typography
										variant="body2"
										sx={{
											color: "text.secondary",
										}}
									>
										CCN: {data.facility_info.provnum}
									</Typography>
								</CardContent>
							</Card>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<Card>
								<CardContent>
									<Typography color="primary" variant="h6" gutterBottom>
										Key Statistics
									</Typography>
									<Grid container spacing={2}>
										<Grid size={{ xs: 6 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Estimated $PPD:
											</Typography>
											<Typography variant="h6" color="primary">
												${data.estimated_ppd.toFixed(2)}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Average Census:
											</Typography>
											<Typography variant="h6" color="primary">
												{data.average_census.toFixed(0)}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Estimated $PPD from OT:
											</Typography>
											<Typography variant="h6" color="primary">
												${data.estimated_ppd_from_ot.toFixed(2)}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Total OT Cost (Annual):
											</Typography>
											<Typography variant="h6" color="primary">
												${(Math.round(data.total_ot_cost / 100) * 100).toLocaleString()}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Estimated $PPD from Agency:
											</Typography>
											<Typography variant="h6" color="primary">
												${data.estimated_ppd_from_agency.toFixed(2)}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Total Cost of Agency (Annual):
											</Typography>
											<Typography variant="h6" color="primary">
												${(Math.round(data.total_cost_of_agency / 100) * 100).toLocaleString()}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Estimated HPPD from Overstaffing:
											</Typography>
											<Typography variant="h6" color="primary">
												{data.estimated_hppd_from_overstaffing.toFixed(2)}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Total Overstaffed Hours (Annual):
											</Typography>
											<Typography variant="h6" color="primary">
												{data.total_overstaffed_hours.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Average Hourly Wage:
											</Typography>
											<Typography variant="h6" color="primary">
												${data.average_hourly_wage.toFixed(0)}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Total Overstaffing Cost (Annual):
											</Typography>
											<Typography variant="h6" color="primary">
												${(Math.round(data.total_overstaffing_cost / 100) * 100).toLocaleString()}
											</Typography>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						</Grid>
					</Grid>

					<Grid container spacing={3} sx={{ mb: 3 }}>
						<Grid size={{ xs: 12 }}>
							<Card>
								<CardContent>
									<Typography color="primary" variant="h6" gutterBottom>
										Estimated Savings (Annual)
									</Typography>
									<Grid container spacing={2}>
										<Grid size={{ xs: 6, md: 3 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Reduction in OT:
											</Typography>
											<Typography
												variant="h6"
												sx={{
													color: "success.main",
												}}
											>
												${data.reduction_in_ot.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6, md: 3 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Reduction in Agency:
											</Typography>
											<Typography
												variant="h6"
												sx={{
													color: "success.main",
												}}
											>
												${data.reduction_in_agency.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6, md: 3 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Reduction in Overstaffing:
											</Typography>
											<Typography
												variant="h6"
												sx={{
													color: "success.main",
												}}
											>
												${data.reduction_in_overstaffing.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
											</Typography>
										</Grid>
										<Grid size={{ xs: 6, md: 3 }}>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
												}}
											>
												Total Estimated Savings:
											</Typography>
											<Typography
												variant="h6"
												sx={{
													color: "success.main",
													fontWeight: 700,
												}}
											>
												${data.total_estimated_savings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
											</Typography>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						</Grid>
					</Grid>

					<Grid container spacing={3}>
						<Grid size={{ xs: 12, lg: 6 }}>
							<Paper sx={{ p: 3 }}>
								<Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "primary.dark" }}>
									HPPD: {startMonth.toFormat("yyyy-MM")} - {endMonth.toFormat("yyyy-MM")}
								</Typography>
								<InHouseLineChart
									leftAxisData={{ headerLabel: "HPPD", dataGroups: hppdDataGroup ? [hppdDataGroup] : [] }}
									rightAxisData={{ headerLabel: "Census", dataGroups: censusDataGroup ? [censusDataGroup] : [] }}
									height="350px"
									width="100%"
									axisColor="var(--inhouse-primary-dark)"
									xTickFormatter={(d) => d.toFormat("MMM d")}
								/>
							</Paper>
						</Grid>
						<Grid size={{ xs: 12, lg: 6 }}>
							<Paper sx={{ p: 3 }}>
								<Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "primary.dark" }}>
									$PPD (estimated): {startMonth.toFormat("yyyy-MM")} - {endMonth.toFormat("yyyy-MM")}
								</Typography>
								<InHouseLineChart
									leftAxisData={{
										headerLabel: "PPD ($)",
										dataGroups: ppdDataGroup ? [ppdDataGroup] : [],
									}}
									rightAxisData={{
										headerLabel: "Agency PPD ($)",
										dataGroups: agencyPpdDataGroup ? [agencyPpdDataGroup] : [],
									}}
									height="350px"
									width="100%"
									axisColor="var(--inhouse-primary-dark)"
									xTickFormatter={(d) => d.toFormat("MMM d")}
								/>
							</Paper>
						</Grid>
					</Grid>

					<Box sx={{ mt: 3, textAlign: "center" }}>
						<Button
							variant="contained"
							color="primary"
							startIcon={<Download />}
							onClick={() =>
								handleDownloadExcel(`pbj_metrics_${data.facility_info.provnum}_${startMonth.toFormat("yyyy-MM")}_${endMonth.toFormat("yyyy-MM")}`, data)
							}
						>
							Download Excel Report
						</Button>
					</Box>
				</Box>
			)}
		</>
	);
};
